import React from "react";
import { useLanguage } from "../../../../contexts";
import { P, useEnergia } from "../engine";
import { Notice, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";

const fmt = (v, d = 0) => P.fmt(v, d);

// Estimación teórica del consumo por equipo y proceso (hoja 02 «estimacion»; hoja 01 filas 4, 5, 49, 88, 114).
// Solo lectura: los datos de placa y régimen se editan en el Inventario de maquinaria.
export function TheoreticalEstimation({ installation }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { f, error } = useEnergia(installation);

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!f) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const { items, total, pendientes } = P.estimacionData(f);
  const facturado = P.energiaAnual(f);
  const cobertura = P.coberturaPct(f); // entero, se compara con el valor mostrado
  const enRango = cobertura != null && cobertura >= P.COB_MIN && cobertura <= P.COB_MAX;
  const top80 = items.filter((x, i) => i === 0 || items[i - 1].cum < 80);
  const usos = P.usosData(f);
  const porProceso = Object.entries(items.reduce((acc, x) => {
    const k = x.m.proceso || "—";
    acc[k] = (acc[k] || 0) + x.kwh;
    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);

  let lectura;
  if (cobertura == null) lectura = t("energy_performance_est_no_invoiced");
  else if (!items.length) lectura = t("energy_performance_est_no_equipment");
  else if (enRango) lectura = t("energy_performance_est_reading_ok").replace("{c}", cobertura).replace("{n}", top80.length);
  else if (cobertura < P.COB_MIN) lectura = t("energy_performance_est_reading_low").replace("{c}", cobertura);
  else lectura = t("energy_performance_est_reading_high").replace("{c}", cobertura - 100);

  return (
    <div className="ep-screen">
      <h3 className="ep-screen__title">{t("energy_performance_est_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_est_intro")}</p>

      <div className="ep-kpis">
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_est_estimated")}</div><div className="ep-kpi__value">{fmt(total / 1000, 1)}</div><div className="ep-kpi__sub">MWh/{t("energy_performance_year")} · {items.length} {t("energy_performance_est_equipment")}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_est_invoiced")}</div><div className="ep-kpi__value">{fmt(facturado / 1000, 1)}</div><div className="ep-kpi__sub">MWh/{t("energy_performance_year")} · {t("energy_performance_last_12_months")}</div></div>
        <div className="ep-kpi">
          <div className="ep-kpi__label">{t("energy_performance_est_coverage")}</div>
          <div className="ep-kpi__value">{cobertura == null ? "—" : `${cobertura}%`}</div>
          <div className="ep-kpi__sub">{cobertura == null ? t("energy_performance_pending") : <StatusBadge estado={enRango ? "Cumple" : "Brecha parcial"}>{enRango ? t("energy_performance_est_in_range") : t("energy_performance_est_out_of_range")}</StatusBadge>}</div>
        </div>
      </div>
      {pendientes.length > 0 && (
        <Notice titulo={t("energy_performance_est_partial").replace("{n}", pendientes.length)}>
          {t("energy_performance_est_partial_note")}: {pendientes.map((m) => m.nombre).join("; ")}
        </Notice>
      )}

      <Section title={t("energy_performance_est_reconciliation")} note={t("energy_performance_est_reconciliation_note")}>
        <p>{lectura}</p>
        {top80.length > 0 && <p className="ep-section__note"><strong>{t("energy_performance_est_top80")}:</strong> {top80.map((x) => x.m.nombre).join(" · ")}</p>}
      </Section>

      <div className="ep-charts"><GraficoCatalogo f={f} id="equipos_pareto" t={t} /><GraficoCatalogo f={f} id="proc_estimado" t={t} /><GraficoCatalogo f={f} id="usos_finales" t={t} /><GraficoCatalogo f={f} id="cobertura" t={t} /></div>
      <Section title={t("energy_performance_est_ranking")}>
        <div className="ep-table">
          <table>
            <thead>
              <tr>
                <th>#</th><th>{t("energy_performance_mach_machine")}</th><th>{t("energy_performance_end_use")}</th><th>{t("energy_performance_process")}</th>
                <th>kW</th><th>{t("energy_performance_mach_units")}</th><th>h/{t("energy_performance_day")}</th><th>{t("energy_performance_mach_days_year")}</th><th>{t("energy_performance_mach_load_factor")}</th>
                <th>kWh/{t("energy_performance_year")}</th><th>%</th><th>{t("energy_performance_cumulative")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x, r) => (
                <tr key={x.i}>
                  <td className="ep-mono">{r + 1}</td>
                  <td><strong>{x.m.nombre}</strong></td>
                  <td>{(P.USO_FINAL.find((z) => z.id === P.usoFinalDe(x.m)) || P.USO_FINAL[0]).nombre.split(" (")[0]}</td>
                  <td>{x.m.proceso}</td>
                  <td className="ep-mono">{fmt(x.m.kW, 1)}</td>
                  <td className="ep-mono">{fmt(x.m.unidades)}</td>
                  <td className="ep-mono">{fmt(x.m.hs_dia, 1)}</td>
                  <td className="ep-mono">{fmt(x.m.dias_anio)}</td>
                  <td className="ep-mono">{fmt(x.m.fc, 2)}</td>
                  <td className="ep-mono">{fmt(x.kwh)}</td>
                  <td className="ep-mono">{fmt(x.pct, 1)}%</td>
                  <td className="ep-mono">{fmt(x.cum, 1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("energy_performance_est_by_process")}>
        <div className="ep-table">
          <table>
            <thead><tr><th>{t("energy_performance_process")}</th><th>kWh/{t("energy_performance_year")}</th><th>%</th></tr></thead>
            <tbody>{porProceso.map(([p, kwh]) => <tr key={p}><td>{p}</td><td className="ep-mono">{fmt(kwh)}</td><td className="ep-mono">{fmt(total ? (kwh / total) * 100 : 0, 1)}%</td></tr>)}</tbody>
          </table>
        </div>
      </Section>

      <Section title={t("energy_performance_end_use_title")} note={t("energy_performance_end_use_note")}>
        <div className="ep-table">
          <table>
            <thead><tr><th>{t("energy_performance_end_use")}</th><th>kWh/{t("energy_performance_year")}</th><th>{t("energy_performance_share_of_plant")}</th><th>{t("energy_performance_where_to_act")}</th></tr></thead>
            <tbody>
              {usos.map((x) => (
                <tr key={x.u.id}>
                  <td><span className="ep-swatch" style={{ background: x.u.color }} />{x.u.nombre}</td>
                  <td className="ep-mono">{fmt(x.kwh)}</td>
                  <td className="ep-mono"><strong>{fmt(x.pct, 1)}%</strong></td>
                  <td className="ep-section__note">{x.u.medida}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("energy_performance_est_method_title")} note={t("energy_performance_est_method_note")} />
    </div>
  );
}
